import { LIMITS } from './inspection/constants';
import { uploadErrorKey } from './inspectionPresentation';

// Browser side of the photo upload (POST /inspection/photos). The site only sends JPEG (re-encoded in a canvas), never HEIC:
// the server stays strict (JPEG / PNG / WEBP, 8 MB) and is not relaxed for us.

export const UPLOAD_LIMITS = Object.freeze({
  imageBytes: 8 * 1024 * 1024,
  attachmentBytes: 5 * 1024 * 1024,
  maxSide: 1600,
  jpegQuality: 0.82
});

const SERVER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// Types the browser may hand over and that a canvas can try to decode (HEIC/HEIF from some phones: converted when decodable).
const DECODABLE_TYPES = [...SERVER_IMAGE_TYPES, 'image/heic', 'image/heif'];
const EXTENSION_TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', pdf: 'application/pdf' };

export class UploadError extends Error {
  constructor(code, httpStatus, message) {
    super(message || code);
    this.code = code;
    this.httpStatus = httpStatus || null;
    this.messageKey = uploadErrorKey(httpStatus, code);
  }
}

// MIME type of a picked file; some browsers leave `type` empty for HEIC, so the extension is a fallback.
export function detectFileType(file) {
  if (!file) return '';
  const declared = String(file.type || '').toLowerCase();
  if (declared) return declared;
  const match = /\.([a-z0-9]+)$/i.exec(String(file.name || ''));
  return (match && EXTENSION_TYPES[match[1].toLowerCase()]) || '';
}

// Cheap checks before any work. kind: 'photo' (vehicle / damage) or 'attachment' (dispute documents: JPG / PNG / PDF, 5 MB).
export function validatePickedFile(file, kind = 'photo') {
  const type = detectFileType(file);
  if (kind === 'attachment') {
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(type)) return { ok: false, error: new UploadError('unsupported', 415) };
    if (file.size > UPLOAD_LIMITS.attachmentBytes) return { ok: false, error: new UploadError('tooLarge', 413) };
    return { ok: true, type };
  }
  if (!DECODABLE_TYPES.includes(type)) return { ok: false, error: new UploadError('unsupported', 415) };
  return { ok: true, type };
}

// Size after scaling so that the long side is at most `maxSide` (never enlarged).
export function computeScaledSize(width, height, maxSide = UPLOAD_LIMITS.maxSide) {
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return null;
  const longSide = Math.max(w, h);
  if (longSide <= maxSide) return { width: Math.round(w), height: Math.round(h) };
  const ratio = maxSide / longSide;
  return { width: Math.max(1, Math.round(w * ratio)), height: Math.max(1, Math.round(h * ratio)) };
}

const jpegName = (name) => `${String(name || 'photo').replace(/\.[a-z0-9]+$/i, '') || 'photo'}.jpg`;

function loadBitmap(file, env) {
  if (env.createImageBitmap) return env.createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => env.createImageBitmap(file));
  return new Promise((resolve, reject) => {
    const url = env.URL.createObjectURL(file);
    const image = new env.Image();
    image.onload = () => { env.URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { env.URL.revokeObjectURL(url); reject(new Error('decode')); };
    image.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

// Converts a picked picture to a JPEG of at most 1600 px (long side) and 8 MB, in a canvas. `env` is injectable for tests.
export async function prepareImage(file, env) {
  const target = env || (typeof window !== 'undefined' ? {
    createImageBitmap: window.createImageBitmap ? window.createImageBitmap.bind(window) : null,
    document: window.document, URL: window.URL, Image: window.Image
  } : null);
  const checked = validatePickedFile(file, 'photo');
  if (!checked.ok) throw checked.error;
  if (!target || !target.document) {
    // No canvas (very old browser): only what the server accepts can go as it is.
    if (SERVER_IMAGE_TYPES.includes(checked.type) && file.size <= UPLOAD_LIMITS.imageBytes) return file;
    throw new UploadError(file.size > UPLOAD_LIMITS.imageBytes ? 'tooLarge' : 'unsupported', 415);
  }
  let bitmap;
  try {
    bitmap = await loadBitmap(file, target);
  } catch (error) {
    throw new UploadError('unsupported', 415);
  }
  const size = computeScaledSize(bitmap.width || bitmap.naturalWidth, bitmap.height || bitmap.naturalHeight);
  if (!size) throw new UploadError('unsupported', 415);
  const canvas = target.document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  // JPEG has no alpha: a white background keeps transparent PNG readable.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, size.width, size.height);
  context.drawImage(bitmap, 0, 0, size.width, size.height);
  if (bitmap.close) bitmap.close();
  let blob = await canvasToBlob(canvas, UPLOAD_LIMITS.jpegQuality);
  if (blob && blob.size > UPLOAD_LIMITS.imageBytes) blob = await canvasToBlob(canvas, 0.6);
  if (!blob) throw new UploadError('unsupported', 415);
  if (blob.size > UPLOAD_LIMITS.imageBytes) throw new UploadError('tooLarge', 413);
  const FileCtor = target.File || (typeof File !== 'undefined' ? File : null);
  return FileCtor ? new FileCtor([blob], jpegName(file.name), { type: 'image/jpeg' }) : blob;
}

// Dispute documents (JPG / PNG / PDF, 5 MB): pictures are compressed like the photos, a PDF goes as it is.
export async function prepareAttachment(file) {
  const checked = validatePickedFile(file, 'attachment');
  if (!checked.ok) throw checked.error;
  if (checked.type === 'application/pdf') return file;
  const prepared = await prepareImage(file);
  if (prepared.size > UPLOAD_LIMITS.attachmentBytes) throw new UploadError('tooLarge', 413);
  return prepared;
}

// Multipart body: the text fields come BEFORE the file (the server reads them while streaming).
export function buildUploadForm({ reservationId, type, kind, file, FormDataImpl }) {
  const Form = FormDataImpl || FormData;
  const form = new Form();
  form.append('reservationId', String(reservationId));
  form.append('type', type);
  if (kind) form.append('kind', kind);
  form.append('file', file, file && file.name ? file.name : 'photo.jpg');
  return form;
}

// Uploads one prepared file. Resolves { id, thumbUrl, url, photos, version } or throws UploadError (message key in `messageKey`).
// The session cookie authenticates the request (same origin): no secret is handled here.
export async function uploadInspectionPhoto({ reservationId, type, kind, file, fetchImpl, FormDataImpl }) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new UploadError('failed', null);
  if (!['checkin', 'checkout', 'attachment'].includes(type)) throw new UploadError('failed', 400);
  const limit = type === 'attachment' ? UPLOAD_LIMITS.attachmentBytes : UPLOAD_LIMITS.imageBytes;
  if (file && file.size > limit) throw new UploadError('tooLarge', 413);
  let response;
  try {
    response = await doFetch('/inspection/photos', {
      method: 'POST',
      credentials: 'same-origin',
      body: buildUploadForm({ reservationId, type, kind, file, FormDataImpl })
    });
  } catch (error) {
    throw new UploadError('network', null);
  }
  let body = null;
  try { body = await response.json(); } catch (error) { body = null; }
  if (!response.ok || !body || Number(body.status) !== 200) {
    const httpStatus = response.status || (body && Number(body.status)) || null;
    const failure = new UploadError('server', httpStatus, body && body.errorMessage);
    failure.serverMessage = (body && body.errorMessage) || null;
    throw failure;
  }
  return { id: body.id, thumbUrl: body.thumbUrl, url: body.photos && body.photos[0] && body.photos[0].url, photos: body.photos || [], version: body.version };
}

// Removes a photo (author only, draft / to_validate). Resolves true or throws UploadError.
export async function removeInspectionPhoto({ photoId, fetchImpl }) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new UploadError('failed', null);
  let response;
  try {
    response = await doFetch('/inspection/photos/remove', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoId })
    });
  } catch (error) {
    throw new UploadError('network', null);
  }
  let body = null;
  try { body = await response.json(); } catch (error) { body = null; }
  if (!response.ok || (body && body.status && Number(body.status) !== 200)) {
    const failure = new UploadError('server', response.status || null, body && body.errorMessage);
    failure.serverMessage = (body && body.errorMessage) || null;
    throw failure;
  }
  return { version: body && body.version !== undefined ? body.version : null };
}

export const MAX_PHOTOS = LIMITS.photoMax;
